import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, MapPin } from "lucide-react";
import { AISetup } from "@/components/AISetup";
import { SYSTEM_PROMPT, checkSupport, isModelCached, loadEngine } from "@/lib/localAI";
import { useAISpot, spotContext } from "@/lib/aiSpot";

const defaultChips = [
  "Quel leurre pour le brochet en mai ?",
  "Pêche à la mouche pour débuter ?",
  "Meilleur moment pour pêcher le sandre ?",
];

const spotChips = (s) => [
  `Quel leurre utiliser à ${s.name} en ce moment ?`,
  `Quelle technique pour le ${s.fish[0].toLowerCase()} ici ?`,
  `Quel matériel prévoir pour ce spot ?`,
];

const WELCOME = { role: "assistant", content: "Bonjour ! Je suis PêcheAI. Posez-moi vos questions sur les techniques, leurres, spots ou la réglementation." };

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const engineRef = useRef(null);
  const checkedRef = useRef(false);
  const scrollRef = useRef(null);
  const spot = useAISpot();
  const quickChips = spot ? spotChips(spot) : defaultChips;

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-ai-assistant", onOpen);
    return () => window.removeEventListener("open-ai-assistant", onOpen);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const start = async () => {
    setStatus("loading");
    try {
      engineRef.current = await loadEngine((r) => {
        setProgress(r.progress || 0);
        setProgressText(r.text || "");
      });
      setStatus("ready");
    } catch (e) {
      console.error(e);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!open || checkedRef.current) return;
    checkedRef.current = true;
    setStatus("checking");
    (async () => {
      if (!(await checkSupport())) return setStatus("unsupported");
      if (await isModelCached()) return start();
      setStatus("idle");
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLast = (fn) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: fn(copy[copy.length - 1].content) };
      return copy;
    });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || status !== "ready") return;
    setInput("");
    setBusy(true);
    const history = [...messages.slice(1), { role: "user", content: msg }].slice(-8);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const stream = await engineRef.current.chat.completions.create({
        messages: [{ role: "system", content: SYSTEM_PROMPT + (spot ? spotContext(spot) : "") }, ...history],
        stream: true,
        temperature: 0.6,
        max_tokens: 400,
      });
      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta?.content;
        if (delta) updateLast((c) => c + delta);
      }
    } catch (e) {
      console.error(e);
      updateLast(() => "Désolé, une erreur est survenue. Réessayez.");
    } finally {
      setBusy(false);
    }
  };

  const ready = status === "ready";

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-assistant-toggle"
        className="fixed bottom-6 right-6 z-[950] flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold pl-4 pr-5 py-3.5 rounded-full shadow-2xl transition-all hover:-translate-y-1"
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        <span className="hidden sm:inline">{open ? "Fermer" : "PêcheAI"}</span>
      </button>

      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-[950] w-[calc(100vw-2rem)] sm:w-96 h-[520px] max-h-[75vh] flex flex-col rounded-3xl bg-white shadow-2xl border border-emerald-900/10 overflow-hidden animate-float-up"
        >
          <div className="bg-emerald-800 text-white p-4 flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-600">
              <MessageCircle className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <p className="font-display font-bold leading-tight">PêcheAI</p>
              {spot ? (
                <p className="text-xs text-amber-300 flex items-center gap-1 truncate" data-testid="ai-spot-context">
                  <MapPin className="w-3 h-3 shrink-0" /> Conseils pour {spot.name}
                </p>
              ) : (
                <p className="text-xs text-emerald-200">IA locale · gratuite · privée</p>
              )}
            </div>
          </div>

          {!ready ? (
            <AISetup status={status} progress={progress} progressText={progressText} onStart={start} />
          ) : (
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-emerald-50/40">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`ai-message-${m.role}`}
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-emerald-700 text-white rounded-br-sm"
                        : "bg-white border border-emerald-900/10 text-slate-700 rounded-bl-sm"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : "")}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="space-y-2 pt-2">
                  {quickChips.map((c) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid="ai-quick-chip"
                      className="block w-full text-left text-sm bg-white border border-emerald-900/10 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-800 px-3 py-2 rounded-xl transition-colors"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="p-3 border-t border-emerald-900/10 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={!ready}
              placeholder={ready ? "Votre question de pêche…" : "Activez l'assistant pour discuter"}
              data-testid="ai-input"
              className="flex-1 bg-emerald-50 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
            />
            <button
              onClick={() => send()}
              disabled={busy || !ready}
              data-testid="ai-send-btn"
              className="grid place-items-center w-10 h-10 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
